import { useState } from "react";
import { Copy, Trash2, Search, X, ChevronDown, ChevronRight, Printer, GripVertical, Plus, Pencil, Check, FolderPlus } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { usePrinterStatus } from "@/hooks/usePrinterStatus";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CaseLabel,
  CaseLabelFolder,
  CaseLabelFormatting,
  useCaseLabels,
  useCaseLabel,
  useCaseLabelFolders,
  useCreateCaseLabelFolder,
  useRenameCaseLabelFolder,
  useDeleteCaseLabelFolder,
  useDeleteCaseLabel,
  useDuplicateCaseLabel,
  useUpdateCaseLabel,
  defaultFormatting,
  templateFormatting4x4,
  templateFormatting2x4,
} from "@/hooks/useCaseLabels";
import { useAllPrintStats, useAllPrintLog, PrintLogEntry } from "@/hooks/usePrintLog";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { CaseLabelEditor } from "@/components/case-labels/CaseLabelEditor";

// ── Consolidated print history ────────────────────────────────────────────

function formatDayHeading(dateStr: string): string {
  const d = new Date(dateStr);
  if (isToday(d))     return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "EEEE, MMM d");
}

function ConsolidatedHistory({
  logs,
  isLoading,
  onLabelClick,
}: {
  logs: PrintLogEntry[];
  isLoading: boolean;
  onLabelClick: (labelId: string | null) => void;
}) {
  if (isLoading) {
    return <p className="text-xs text-muted-foreground px-3 py-3">Loading…</p>;
  }
  if (logs.length === 0) {
    return (
      <p className="text-xs text-muted-foreground px-3 py-6 text-center leading-relaxed">
        No prints recorded yet.
      </p>
    );
  }

  // Group by calendar day
  const days: { heading: string; date: Date; entries: PrintLogEntry[] }[] = [];
  for (const log of logs) {
    const d = new Date(log.printed_at);
    const last = days[days.length - 1];
    if (last && isSameDay(d, last.date)) {
      last.entries.push(log);
    } else {
      days.push({ heading: formatDayHeading(log.printed_at), date: d, entries: [log] });
    }
  }

  return (
    <div className="py-1">
      {days.map((day) => {
        const dayTotal = day.entries.reduce((s, e) => s + e.quantity, 0);
        return (
          <div key={day.heading}>
            {/* Day heading */}
            <div className="flex items-center justify-between px-2 pt-2.5 pb-0.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/70">
                {day.heading}
              </span>
              <span className="text-[10px] text-muted-foreground/50">
                {dayTotal.toLocaleString()} labels
              </span>
            </div>
            {/* Entries */}
            {day.entries.map((log) => (
              <div
                key={log.id}
                className={`px-2 py-1.5 hover:bg-muted/50 transition-colors ${
                  log.label_id ? "cursor-pointer" : ""
                }`}
                onClick={() => log.label_id && onLabelClick(log.label_id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-medium truncate leading-tight">
                    {log.label_name || "Untitled"}
                  </p>
                  <span className="text-[11px] tabular-nums text-muted-foreground shrink-0">
                    ×{log.quantity.toLocaleString()}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground/60">
                  {format(new Date(log.printed_at), "h:mm a")} · {log.size}
                </p>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── New-label config ──────────────────────────────────────────────────────

interface NewConfig {
  size: "4x4" | "2x4";
  formatting: CaseLabelFormatting;
  fromTemplate: boolean;
  initialFields?: import("@/hooks/useCaseLabels").CaseLabelFields;
  initialFolderId?: string | null;
}

function buildConfig(size: "4x4" | "2x4", useTemplate: boolean, initialFields?: NewConfig["initialFields"]): NewConfig {
  const formatting = useTemplate
    ? size === "4x4" ? templateFormatting4x4() : templateFormatting2x4()
    : defaultFormatting(size);
  return { size, formatting, fromTemplate: useTemplate, initialFields };
}

// ── Label thumbnail SVG ───────────────────────────────────────────────────

function LabelThumbnail({
  size,
  template,
  scale = 1,
}: {
  size: "4x4" | "2x4";
  template: boolean;
  scale?: number;
}) {
  const bw = 100 * scale;
  const bh = (size === "4x4" ? 100 : 50) * scale;
  const m  = 8 * scale;
  const cx = bw / 2;

  // Map label dot coordinates (812 wide) → thumbnail px
  const d = (dots: number) => dots * (bw / 812);

  const fill = "#1a1a1a";
  const ff   = "system-ui, sans-serif";

  let content: React.ReactNode;

  if (template && size === "4x4") {
    content = <>
      <text x={cx}   y={d(115)} textAnchor="middle" fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.65}>Pisoni Vineyards</text>
      <text x={cx}   y={d(255)} textAnchor="middle" fontSize={d(110)} fontFamily={ff} fill={fill} opacity={0.92} fontWeight="bold">LOT 2401</text>
      <text x={cx}   y={d(375)} textAnchor="middle" fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.78}>2022</text>
      <text x={cx}   y={d(455)} textAnchor="middle" fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.72}>Pinot Noir</text>
      <text x={cx}   y={d(535)} textAnchor="middle" fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.65}>Estate</text>
      <text x={cx}   y={d(660)} textAnchor="middle" fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.65}>Santa Lucia Highlands</text>
      <text x={m}    y={d(742)} textAnchor="start"  fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.45}>Alc. 14.2% by Vol.</text>
      <text x={bw-m} y={d(742)} textAnchor="end"    fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.45}>12 × 750mL</text>
      <text x={m}    y={d(772)} textAnchor="start"  fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.4}>Btld: 04.15.25</text>
      <text x={cx}   y={d(772)} textAnchor="middle" fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.4}>FDA# 12345</text>
      <text x={bw-m} y={d(772)} textAnchor="end"    fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.4}>BW-1234</text>
    </>;
  } else if (template && size === "2x4") {
    const lx = m;
    const rx = bw * 0.63;
    content = <>
      <text x={lx} y={d(55)}  fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.9} fontWeight="bold">LOT 2401</text>
      <text x={lx} y={d(130)} fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.75}>Estate</text>
      <text x={lx} y={d(210)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.6}>Pisoni Vineyards</text>
      <text x={lx} y={d(250)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.55}>2022</text>
      <text x={lx} y={d(290)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.5}>S. Lucia Highlands</text>
      <text x={lx} y={d(330)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.5}>Pinot Noir</text>
      <text x={rx} y={d(55)}  fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.5}>Btld: 04.15.25</text>
      <text x={rx} y={d(95)}  fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.5}>12 × 750mL</text>
      <text x={rx} y={d(135)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.45}>BW-1234</text>
      <text x={rx} y={d(175)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.45}>Alc. 14.2%</text>
    </>;
  } else if (!template && size === "4x4") {
    content = <>
      <text x={m} y={d(75)}  fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.65}>Pisoni Vineyards</text>
      <text x={m} y={d(175)} fontSize={d(110)} fontFamily={ff} fill={fill} opacity={0.9} fontWeight="bold">LOT 2401</text>
      <text x={m} y={d(280)} fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.7}>2022 · Pinot Noir</text>
      <text x={m} y={d(370)} fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.55}>Santa Lucia Highlands</text>
    </>;
  } else {
    // 2x4 blank
    content = <>
      <text x={m} y={d(65)}  fontSize={d(48)}  fontFamily={ff} fill={fill} opacity={0.9} fontWeight="bold">LOT 2401</text>
      <text x={m} y={d(155)} fontSize={d(72)}  fontFamily={ff} fill={fill} opacity={0.7}>Estate</text>
      <text x={m} y={d(225)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.55}>Pisoni Vineyards · 2022</text>
      <text x={m} y={d(280)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.45}>Santa Lucia Highlands</text>
      <text x={m} y={d(335)} fontSize={d(24)}  fontFamily={ff} fill={fill} opacity={0.4}>Pinot Noir</text>
    </>;
  }

  return (
    <svg
      width={bw}
      height={bh}
      viewBox={`0 0 ${bw} ${bh}`}
      className="rounded border border-border/60 bg-white shadow-sm overflow-hidden"
    >
      {content}
    </svg>
  );
}

// ── Inline start screen ───────────────────────────────────────────────────

const START_OPTIONS: {
  size: "4x4" | "2x4";
  template: boolean;
  title: string;
  desc: string;
}[] = [
  { size: "4x4", template: false, title: "4×4 Blank",    desc: "Empty canvas — position fields freely" },
  { size: "4x4", template: true,  title: "4×4 Template", desc: "Pre-arranged centered layout"          },
  { size: "2x4", template: false, title: "2×4 Blank",    desc: "Landscape, empty canvas"               },
  { size: "2x4", template: true,  title: "2×4 Template", desc: "Landscape 2-column layout"             },
];

function StartScreen({ onPick }: { onPick: (cfg: NewConfig) => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 p-10 bg-muted/20">
      <div className="text-center">
        <h2 className="text-lg font-semibold tracking-tight">New label</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose a size and starting point
        </p>
      </div>

      <div className="grid grid-cols-2 gap-5 w-full max-w-lg">
        {START_OPTIONS.map((opt) => (
          <button
            key={`${opt.size}-${opt.template}`}
            onClick={() => onPick(buildConfig(opt.size, opt.template))}
            className="group flex flex-col items-center gap-4 rounded-xl border-2 border-border bg-background hover:border-primary hover:shadow-md p-6 transition-all text-center"
          >
            <LabelThumbnail size={opt.size} template={opt.template} scale={1.5} />
            <div>
              <p className="text-sm font-semibold group-hover:text-primary transition-colors">
                {opt.title}
              </p>
              <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                {opt.desc}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Draggable label row ───────────────────────────────────────────────────

interface DraggableLabelRowProps {
  label: CaseLabel;
  isActive: boolean;
  stats: { lastDate: string; lastQty: number } | undefined;
  onSelect: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  isPendingDuplicate: boolean;
}

function DraggableLabelRow({
  label, isActive, stats, onSelect, onDuplicate, onDelete, isPendingDuplicate,
}: DraggableLabelRowProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: label.id });

  function fmt(dateStr: string) {
    const d = new Date(dateStr);
    if (isToday(d))     return "Today";
    if (isYesterday(d)) return "Yesterday";
    return format(d, "MMM d");
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className={`group flex items-center gap-1 px-2 py-2 transition-colors ${
        isActive ? "bg-accent text-accent-foreground" : "hover:bg-muted/50 text-foreground"
      } ${isDragging ? "opacity-40" : "cursor-pointer"}`}
      onClick={() => { if (!isDragging) onSelect(label.id); }}
    >
      {/* Drag handle — hidden until hover */}
      <span
        {...listeners}
        title="Drag to move to a different folder"
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing shrink-0 touch-none opacity-0 group-hover:opacity-40 hover:!opacity-80 transition-opacity"
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate leading-tight">{label.name}</p>
        {stats && (
          <p className="text-[10px] text-muted-foreground/60 truncate leading-tight">
            {fmt(stats.lastDate)} · {stats.lastQty.toLocaleString()} labels
          </p>
        )}
      </div>

      <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-5 shrink-0 opacity-70">
        {label.label_size}
      </Badge>

      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button
          title="Duplicate"
          onClick={(e) => { e.stopPropagation(); onDuplicate(label.id); }}
          disabled={isPendingDuplicate}
          className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
        >
          <Copy className="h-3 w-3" />
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              title="Delete"
              onClick={(e) => e.stopPropagation()}
              className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete label?</AlertDialogTitle>
              <AlertDialogDescription>"{label.name}" will be permanently deleted.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => onDelete(label.id)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ── Droppable folder group ───────────────────────────────────────────────

function DroppableFolderGroup({
  folder,
  itemCount,
  isCollapsed,
  onToggle,
  onNewForFolder,
  onRename,
  onDelete,
  showDrop,
  children,
}: {
  folder: { id: string | null; name: string };
  itemCount: number;
  isCollapsed: boolean;
  onToggle: (folderId: string) => void;
  onNewForFolder: (folderId: string | null) => void;
  onRename: (folderId: string, name: string) => void;
  onDelete: (folderId: string) => void;
  showDrop: boolean;
  children: React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(folder.name);
  const canManage = folder.id !== null;

  const { setNodeRef, isOver } = useDroppable({
    id: `folder:${folder.id ?? "unfiled"}`,
    data: { folderId: folder.id },
  });

  const highlight = isOver && showDrop;

  function commitRename() {
    setEditing(false);
    const trimmed = draftName.trim();
    if (folder.id && trimmed && trimmed !== folder.name) onRename(folder.id, trimmed);
    else setDraftName(folder.name);
  }

  return (
    <div ref={setNodeRef} className={`rounded transition-colors ${highlight ? "bg-primary/5 outline outline-1 outline-primary/20" : ""}`}>
      <div className="flex items-center group/hdr">
        {editing ? (
          <div className="flex items-center gap-1 px-2 pt-2 pb-0.5 flex-1 min-w-0">
            <Input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") { setDraftName(folder.name); setEditing(false); }
              }}
              onBlur={commitRename}
              className="h-6 text-xs px-1 py-0"
            />
          </div>
        ) : (
          <button
            onClick={() => folder.id && onToggle(folder.id)}
            className="flex items-center gap-1 px-2 pt-2 pb-0.5 text-left flex-1 min-w-0"
          >
            {isCollapsed
              ? <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
              : <ChevronDown  className="h-3 w-3 text-muted-foreground/50 shrink-0" />
            }
            <span className={`text-xs uppercase tracking-wider font-semibold truncate transition-colors ${
              highlight ? "text-primary" : "text-muted-foreground/70"
            }`}>
              {folder.name}
            </span>
            <span className="ml-1 text-xs text-muted-foreground/40 shrink-0">{itemCount}</span>
          </button>
        )}
        {!editing && (
          <div className="flex items-center opacity-0 group-hover/hdr:opacity-60 hover:[&>*]:!opacity-100 transition-opacity">
            {canManage && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setDraftName(folder.name); setEditing(true); }}
                  title={`Rename ${folder.name}`}
                  className="mr-1 pt-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                {itemCount === 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        title={`Delete empty folder ${folder.name}`}
                        className="mr-1 pt-1.5 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete folder?</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{folder.name}" will be permanently deleted. It's empty, so no labels are affected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => folder.id && onDelete(folder.id)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onNewForFolder(folder.id); }}
              title={`New label in ${folder.name}`}
              className="mr-1.5 pt-1.5 text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      {!isCollapsed && children}
      {highlight && (
        <p className="text-[10px] text-primary/60 text-center pb-1 italic">Drop to move here</p>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function CaseLabels() {
  const [editingId,   setEditingId]   = useState<string | null>(null);
  // null = show start screen; non-null = show editor for new label
  const [startConfig, setStartConfig] = useState<NewConfig | null>(null);
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [sidebarTab, setSidebarTab]   = useState<"labels" | "history">("labels");

  const toggleGroup = (folderId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const { data: labels = [], isLoading } = useCaseLabels();
  const { data: folders = [] }           = useCaseLabelFolders();
  const { data: printStats }             = useAllPrintStats();
  const { data: allPrintLog = [], isLoading: logLoading } = useAllPrintLog();
  const { status: printerStatus, ribbonPercent } = usePrinterStatus();
  const deleteLabel    = useDeleteCaseLabel();
  const duplicateLabel = useDuplicateCaseLabel();
  const updateLabel    = useUpdateCaseLabel();
  const createFolder   = useCreateCaseLabelFolder();
  const renameFolder   = useRenameCaseLabelFolder();
  const deleteFolder   = useDeleteCaseLabelFolder();

  const folderName = (id: string | null) => (id ? folders.find((f) => f.id === id)?.name ?? "Unfiled" : "Unfiled");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function onDragStart(e: DragStartEvent) {
    setDraggingId(e.active.id as string);
  }

  function onDragEnd(e: DragEndEvent) {
    setDraggingId(null);
    const { active, over } = e;
    if (!over) return;
    const labelId = active.id as string;
    if (!("folderId" in (over.data.current ?? {}))) return;
    const newFolderId = (over.data.current?.folderId as string | null | undefined) ?? null;
    const label = labels.find((l) => l.id === labelId);
    if (!label) return;
    if ((label.folder_id ?? null) === newFolderId) return;
    updateLabel.mutate({ id: labelId, folder_id: newFolderId });
  }

  const showingEditor = editingId !== null || startConfig !== null;

  const handleSaved = (id: string) => setEditingId(id);

  const handleDuplicate = async (id: string) => {
    const result = await duplicateLabel.mutateAsync(id);
    setEditingId(result.id);
    setStartConfig(null);
  };

  const handleSelectLabel = (id: string) => {
    setEditingId(id);
    setStartConfig(null);
  };

  const handleNewLabel = () => {
    // Go back to start screen
    setEditingId(null);
    setStartConfig(null);
  };

  const handlePick = (cfg: NewConfig) => {
    setStartConfig(cfg);
    setEditingId(null);
  };

  const handleNewForFolder = (folderId: string | null) => {
    handlePick({ ...buildConfig("4x4", true), initialFolderId: folderId });
  };

  const handleCreateFolder = async () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) return;
    await createFolder.mutateAsync(trimmed);
    setNewFolderName("");
    setNewFolderOpen(false);
  };

  // Filter + group
  const q = search.trim().toLowerCase();
  const filtered = q
    ? labels.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          folderName(l.folder_id).toLowerCase().includes(q)
      )
    : labels;

  const groups: { folderId: string | null; name: string; items: CaseLabel[] }[] = [];
  if (q) {
    groups.push({ folderId: null, name: "", items: filtered });
  } else {
    const map = new Map<string | null, CaseLabel[]>();
    // Every real folder gets a slot up front — folders can exist and show up empty,
    // like real file folders, even before any label is filed into them.
    for (const f of folders) map.set(f.id, []);
    for (const l of filtered) {
      const key = l.folder_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(l);
    }
    // Named folders first (alphabetical), unfiled last
    const namedIds = [...map.keys()].filter((k): k is string => k !== null);
    namedIds
      .sort((a, b) => folderName(a).localeCompare(folderName(b)))
      .forEach((id) => groups.push({ folderId: id, name: folderName(id), items: map.get(id)! }));
    // "Unfiled" is a virtual bucket, not a real folder — only show it when it has labels.
    if (map.has(null) && map.get(null)!.length > 0) {
      groups.push({ folderId: null, name: "Unfiled", items: map.get(null)! });
    }
  }

  const draggingLabel = draggingId ? labels.find((l) => l.id === draggingId) ?? null : null;

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Left sidebar ──────────────────────────────────────────────── */}
      <div className="w-56 shrink-0 border-r flex flex-col bg-background">
        {/* Header */}
        <div className="px-3 py-3 border-b flex items-center gap-2 shrink-0">
          <SidebarTrigger className="-ml-1" />
          <span className="text-sm font-semibold flex-1 truncate">Case Labels</span>
        </div>

        {/* Tab toggle */}
        <div className="flex border-b shrink-0">
          <button
            onClick={() => setSidebarTab("labels")}
            className={`flex-1 text-[11px] font-medium py-1.5 transition-colors ${
              sidebarTab === "labels"
                ? "text-foreground border-b-2 border-primary -mb-px"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Labels
          </button>
          <button
            onClick={() => setSidebarTab("history")}
            className={`flex-1 text-[11px] font-medium py-1.5 transition-colors ${
              sidebarTab === "history"
                ? "text-foreground border-b-2 border-primary -mb-px"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            History
          </button>
        </div>

        {/* Search — labels tab only */}
        {sidebarTab === "labels" && labels.length > 0 && (
          <div className="px-2 py-1.5 border-b shrink-0">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="h-7 pl-6 pr-6 text-xs border-none bg-muted/50 shadow-none focus-visible:ring-0"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* New folder — labels tab only */}
        {sidebarTab === "labels" && (
          <div className="px-2 py-1 border-b shrink-0">
            {newFolderOpen ? (
              <div className="flex items-center gap-1">
                <Input
                  autoFocus
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateFolder();
                    if (e.key === "Escape") { setNewFolderName(""); setNewFolderOpen(false); }
                  }}
                  className="h-6 text-xs px-1.5"
                />
                <button
                  onClick={handleCreateFolder}
                  disabled={createFolder.isPending}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setNewFolderOpen(true)}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground px-0.5 py-0.5"
              >
                <FolderPlus className="h-3 w-3" />
                New folder
              </button>
            )}
          </div>
        )}

        {/* Label list / History feed */}
        <div className="flex-1 overflow-y-auto py-1">
          {sidebarTab === "history" ? (
            <ConsolidatedHistory
              logs={allPrintLog}
              isLoading={logLoading}
              onLabelClick={(id) => id && handleSelectLabel(id)}
            />
          ) : isLoading ? (
            <p className="text-xs text-muted-foreground px-3 py-3">Loading…</p>
          ) : filtered.length === 0 && (q || folders.length === 0) ? (
            <p className="text-xs text-muted-foreground px-3 py-6 text-center leading-relaxed">
              {search ? "No matches." : "No labels yet."}
            </p>
          ) : (
            <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
              {groups.map(({ folderId, name, items }) => {
                const isCollapsed = folderId ? collapsed.has(folderId) : false;
                const rows = items.map((label) => (
                  <DraggableLabelRow
                    key={label.id}
                    label={label}
                    isActive={editingId === label.id}
                    stats={printStats?.get(label.id)}
                    onSelect={handleSelectLabel}
                    onDuplicate={handleDuplicate}
                    onDelete={(id) => {
                      deleteLabel.mutate(id);
                      if (editingId === id) handleNewLabel();
                    }}
                    isPendingDuplicate={duplicateLabel.isPending}
                  />
                ));

                if (!name) return <div key="ungrouped">{rows}</div>;

                const isDifferentFolder = draggingLabel
                  ? (draggingLabel.folder_id ?? null) !== folderId
                  : false;

                return (
                  <DroppableFolderGroup
                    key={folderId ?? "unfiled"}
                    folder={{ id: folderId, name }}
                    itemCount={items.length}
                    isCollapsed={isCollapsed}
                    onToggle={toggleGroup}
                    onNewForFolder={handleNewForFolder}
                    onRename={(id, newName) => renameFolder.mutate({ id, name: newName })}
                    onDelete={(id) => deleteFolder.mutate(id)}
                    showDrop={isDifferentFolder}
                  >
                    {rows}
                  </DroppableFolderGroup>
                );
              })}

              <DragOverlay dropAnimation={null}>
                {draggingLabel && (
                  <div className="flex items-center gap-1.5 px-2 py-2 bg-background border rounded-md shadow-lg cursor-grabbing max-w-[200px]">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                    <span className="text-sm font-medium truncate">{draggingLabel.name}</span>
                    <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-5 shrink-0">
                      {draggingLabel.label_size}
                    </Badge>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>

      {/* ── Right panel ───────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 overflow-hidden flex flex-col">

        {/* Persistent header — visible only on start screen */}
        {!showingEditor && (
          <div className="border-b bg-background/80 backdrop-blur-sm shrink-0">
            <div className="px-4 py-2.5 flex items-center gap-2">
              <span className="text-sm font-semibold text-muted-foreground">New Label</span>

              <div className="flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePick(buildConfig("4x4", false))}
                  className="px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Blank 4×4
                </button>
                <button
                  type="button"
                  onClick={() => handlePick(buildConfig("2x4", false))}
                  className="border-l px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Blank 2×4
                </button>
              </div>

              <div className="flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => handlePick(buildConfig("4x4", true))}
                  className="px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Template 4×4
                </button>
                <button
                  type="button"
                  onClick={() => handlePick(buildConfig("2x4", true))}
                  className="border-l px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Template 2×4
                </button>
              </div>

              <div className="ml-auto flex items-center gap-1.5">
                <span
                  className="flex items-center gap-1"
                  title={
                    printerStatus === "online"
                      ? `Printer online (192.168.1.77)${ribbonPercent !== null ? ` · Ribbon: ${ribbonPercent}%` : ""}`
                      : printerStatus === "offline"
                      ? "Printer offline — check network"
                      : "Checking printer…"
                  }
                >
                  <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${
                    printerStatus === "online"  ? (
                      ribbonPercent !== null && ribbonPercent < 10 ? "bg-red-500"   :
                      ribbonPercent !== null && ribbonPercent < 30 ? "bg-amber-400" :
                      "bg-green-500"
                    ) :
                    printerStatus === "offline" ? "bg-red-500" :
                    "bg-muted-foreground/40 animate-pulse"
                  }`} />
                  <span className={`text-[10px] font-medium tabular-nums leading-none ${
                    printerStatus !== "online" || ribbonPercent === null ? "text-muted-foreground/30" :
                    ribbonPercent < 10 ? "text-destructive" :
                    ribbonPercent < 30 ? "text-amber-500"   :
                    "text-muted-foreground"
                  }`}>
                    {printerStatus === "online" && ribbonPercent !== null ? `${ribbonPercent}%` : "—%"}
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden">
          {editingId === null && startConfig === null ? (
            <StartScreen onPick={handlePick} />
          ) : (
            <EditorWrapper
              editingId={editingId}
              startConfig={startConfig}
              onSaved={handleSaved}
              onNew={handlePick}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Editor wrapper ────────────────────────────────────────────────────────

function EditorWrapper({
  editingId,
  startConfig,
  onSaved,
  onNew,
}: {
  editingId: string | null;
  startConfig: NewConfig | null;
  onSaved: (id: string) => void;
  onNew: (cfg: NewConfig) => void;
}) {
  const { data: label, isLoading } = useCaseLabel(editingId);

  if (editingId && isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  const cfg = startConfig ?? buildConfig("4x4", false);

  return (
    <CaseLabelEditor
      key={editingId ?? `new-${cfg.size}-${JSON.stringify(cfg.formatting).length}`}
      label={label}
      onSaved={onSaved}
      onNew={(size, formatting, fromTemplate) => onNew({ size, formatting, fromTemplate: fromTemplate ?? false })}
      initialSize={cfg.size}
      initialFormatting={cfg.formatting}
      initialFields={cfg.initialFields}
      initialFolderId={cfg.initialFolderId}
      initialShowGhosts={cfg.fromTemplate}
    />
  );
}
